import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const val = (obj, key, fallback = '') => {
  const v = obj?.[key];
  if (v === null || v === undefined) return fallback;
  return String(v);
};

const firstRow = (layoutRows, idx) => {
  const rows = Array.isArray(layoutRows?.[idx]) ? layoutRows[idx] : [];
  return rows?.[0] || {};
};

const rowsOf = (layoutRows, idx) => (Array.isArray(layoutRows?.[idx]) ? layoutRows[idx] : []);

const formatKoreanDate = (raw) => {
  if (!raw) return '';
  const s = String(raw).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return s;
  return `${m[1]}년 ${Number(m[2])}월 ${Number(m[3])}일`;
};

const toAmountNumber = (raw) => {
  const s = String(raw ?? '').replace(/[^\d]/g, '');
  const n = Number(s || 0);
  return Number.isFinite(n) ? n : 0;
};

const formatNumber = (n) => {
  try {
    return new Intl.NumberFormat('ko-KR').format(n);
  } catch (e) {
    return String(n);
  }
};

export default function PocProgramSupportPreview({ signatureDataUrl, structureJson, dataJson }) {
  const { t } = useTranslation();
  const layoutRows = dataJson?.layoutRows || {};

  // 0) 상단(로고)
  const topRow = firstRow(layoutRows, 0);
  const logo = val(topRow, 'support_logo', '').trim();

  // 1) 기본정보
  const infoRow = firstRow(layoutRows, 1);

  // 2) 참여인원(복수 행)
  const participantRows = rowsOf(layoutRows, 2);

  // 3) 학생 연구원 역할(복수 행)
  const roleRows = rowsOf(layoutRows, 3);

  // 4~11) 본문 텍스트들(각 1행)
  const txt = (idx, key) => val(firstRow(layoutRows, idx), key, '').trim();
  const background = txt(4, 'background_text');
  const solution = txt(5, 'solution_text');
  const goal = txt(6, 'goal_text');
  const method = txt(7, 'method_text');
  const data = txt(8, 'data_text');
  const expected = txt(9, 'expected_text');
  const academic = txt(10, 'academic_text');
  const industry = txt(11, 'industry_text');

  // 12) 지원비 세부 사용계획(복수 행) - 화면은 6줄 고정
  const budgetRows = rowsOf(layoutRows, 12);

  // 13) 기타(제출일, 담당교수명)
  const dividerRow = firstRow(layoutRows, 13);
  const submitDate = formatKoreanDate(val(dividerRow, 'submit_date', '').trim());
  const dividerProfName = val(dividerRow, 'prof_name', '').trim();

  const budgetFixedRows = useMemo(() => {
    const base = (budgetRows || []).map((r) => ({
      spend_date: val(r, 'spend_date', '').trim(),
      spend_item: val(r, 'spend_item', '').trim(),
      spend_detail: val(r, 'spend_detail', '').trim(),
      spend_amount: val(r, 'spend_amount', '').trim(),
      spend_note: val(r, 'spend_note', '').trim(),
    }));
    while (base.length < 6) base.push({ spend_date: '', spend_item: '', spend_detail: '', spend_amount: '', spend_note: '' });
    return base.slice(0, 6);
  }, [budgetRows]);

  const totalAmount = useMemo(() => {
    return budgetFixedRows.reduce((acc, r) => acc + toAmountNumber(r.spend_amount), 0);
  }, [budgetFixedRows]);

  const profName = val(infoRow, 'prof_name', '').trim();

  return (
    <div className="poc-preview-shell dfb-preview-shell support-preview-shell">
      {/* 단일 페이지(연속 문서) */}
      <div className="poc-preview-page dfb-preview-page support-preview-page">
        <div className="poc-preview support-preview">
          <div className="support-topline">[서식1] 산학협력 PoC프로그램 수행 지원 신청서</div>

          {/* 상단 타이틀 박스 ~ 기대효과: 단일 테이블 */}
          <table className="poc-table support-info-table support-unified-table">
            <colgroup>
              <col style={{ width: '15%' }} />
              <col style={{ width: '35%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '35%' }} />
            </colgroup>
            <tbody>
              <tr>
                <td className="support-head-logo" colSpan={1}>
                  {logo ? (
                    <img src={logo} alt="support-logo" />
                  ) : (
                    <div className="support-head-logo-placeholder" aria-hidden="true">
                      G
                    </div>
                  )}
                </td>
                <td className="support-head-titles" colSpan={3}>
                  <div className="support-title-main">PoC프로그램 수행 지원 신청</div>
                </td>
              </tr>
              {/* 과제명 / 팀명 */}
              <tr>
                <td className="poc-label">과제명</td>
                <td className="poc-value support-prewrap">{val(infoRow, 'task_name')}</td>
                <td className="poc-label">팀명</td>
                <td className="poc-value">{val(infoRow, 'team_name')}</td>
              </tr>
              <tr>
                <td className="poc-label">협력기관</td>
                <td className="poc-value" colSpan={3}>{val(infoRow, 'partner_org')}</td>
              </tr>
              <tr>
                <td className="poc-label">담당 교수</td>
                <td className="poc-value">{val(infoRow, 'prof_name')}</td>
                <td className="poc-label">담당 교수 연락처</td>
                <td className="poc-value">{val(infoRow, 'prof_phone')}</td>
              </tr>
              <tr>
                <td className="poc-label">대표 학생</td>
                <td className="poc-value">{val(infoRow, 'student_leader')}</td>
                <td className="poc-label">대표 학생 연락처</td>
                <td className="poc-value">{val(infoRow, 'student_phone')}</td>
              </tr>

              {/* 참여인원 */}
              {(() => {
                const dataRows = (participantRows?.length ? participantRows : [{}]).slice(0, 2); // 캡쳐는 2행(멘토/학부생)
                const rowSpan = 1 + dataRows.length;
                return (
                  <>
                    <tr>
                      <td className="poc-cat" rowSpan={rowSpan}>참여인원</td>
                      <td className="poc-label">구분</td>
                      <td className="poc-label">인원 수</td>
                      <td className="poc-label">성명(모두 기재)</td>
                    </tr>
                    {dataRows.map((r, i) => (
                      <tr key={`support-part-${i}`}>
                        <td className="poc-value">{val(r, 'participant_type')}</td>
                        <td className="poc-value" style={{ textAlign: 'center' }}>{val(r, 'participant_count')}</td>
                        <td className="poc-value">{val(r, 'participant_names')}</td>
                      </tr>
                    ))}
                  </>
                );
              })()}

              {/* 학생 연구원 역할 */}
              {(() => {
                const dataRows = (roleRows?.length ? roleRows : [{}]).slice(0, 3); // 캡쳐는 3행
                const rowSpan = dataRows.length || 1;
                return (
                  <>
                    {dataRows.map((r, i) => (
                      <tr key={`support-role-${i}`}>
                        {i === 0 && <td className="poc-cat" rowSpan={rowSpan}>학생 연구원<br />역할</td>}
                        <td className="poc-value" style={{ textAlign: 'center' }}>{val(r, 'student_name')}</td>
                        <td className="poc-value support-prewrap" colSpan={2}>{val(r, 'student_role_desc')}</td>
                      </tr>
                    ))}
                  </>
                );
              })()}
              <tr>
                <td className="support-section-title" colSpan={4}>추진배경</td>
              </tr>
              <tr>
                <td className="support-section-body support-prewrap" colSpan={4}>{background}</td>
              </tr>

              <tr>
                <td className="support-section-title" colSpan={4}>해결 방안</td>
              </tr>
              <tr>
                <td className="support-section-body support-prewrap" colSpan={4}>{solution}</td>
              </tr>

              <tr>
                <td className="support-section-title" colSpan={4}>목표 및 내용</td>
              </tr>
              <tr>
                <td className="support-section-body support-prewrap" colSpan={4}>{goal}</td>
              </tr>

              <tr>
                <td className="support-section-title" colSpan={4}>핵심 방법론</td>
              </tr>
              <tr>
                <td className="support-section-body support-prewrap" colSpan={4}>{method}</td>
              </tr>

              <tr>
                <td className="support-section-title" colSpan={4}>데이터</td>
              </tr>
              <tr>
                <td className="support-section-body support-prewrap" colSpan={4}>{data}</td>
              </tr>

              <tr>
                <td className="support-section-title" colSpan={4}>기대효과</td>
              </tr>
              <tr>
                <td className="support-section-body support-prewrap" colSpan={4}>{expected}</td>
              </tr>

              {!!academic && (
                <>
                  <tr>
                    <td className="support-section-title" colSpan={4}>학술·기술적 기여</td>
                  </tr>
                  <tr>
                    <td className="support-section-body support-prewrap" colSpan={4}>{academic}</td>
                  </tr>
                </>
              )}
              {!!industry && (
                <>
                  <tr>
                    <td className="support-section-title" colSpan={4}>산업·사회적 활용</td>
                  </tr>
                  <tr>
                    <td className="support-section-body support-prewrap" colSpan={4}>{industry}</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>

          <div className="support-bullet-title">○ 지원비 세부 사용계획</div>

          <table className="poc-table support-budget-table">
            <colgroup>
              <col style={{ width: '6%' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '30%' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '16%' }} />
            </colgroup>
            <thead>
              <tr>
                <th className="poc-label">연번</th>
                <th className="poc-label">지출예정일자</th>
                <th className="poc-label">항목</th>
                <th className="poc-label">진행 세부내역</th>
                <th className="poc-label">예상금액</th>
                <th className="poc-label">비고(결제방법)</th>
              </tr>
            </thead>
            <tbody>
              {budgetFixedRows.map((r, idx) => (
                <tr key={`support-budget-${idx}`}>
                  <td className="poc-value" style={{ textAlign: 'center' }}>{idx + 1}</td>
                  <td className="poc-value">{r.spend_date}</td>
                  <td className="poc-value">{r.spend_item}</td>
                  <td className="poc-value">{r.spend_detail}</td>
                  <td className="poc-value" style={{ textAlign: 'right' }}>{r.spend_amount ? formatNumber(toAmountNumber(r.spend_amount)) : ''}</td>
                  <td className="poc-value">{r.spend_note}</td>
                </tr>
              ))}
              <tr>
                <td className="poc-cat support-total-label" colSpan={4} style={{ textAlign: 'right' }}>사용금액 합계</td>
                <td className="poc-cat support-total-amount" style={{ textAlign: 'right' }}>{formatNumber(totalAmount)} 원</td>
                <td className="poc-cat"></td>
              </tr>
            </tbody>
          </table>

          <div className="support-submit-date">{submitDate}</div>

          <div className="support-sign-row">
            <div className="support-sign-name">
              <span className="support-sign-prof">{t('projects.apply.previewFooter.advisor')}</span>
              <span className="name">{dividerProfName || profName}</span>
              <span className="in">{t('projects.apply.previewFooter.seal')}</span>
              {signatureDataUrl && <img src={signatureDataUrl} alt="signature" />}
            </div>
          </div>

          <div className="support-to">한국외국어대학교&nbsp;&nbsp;G-RISE사업단장 귀하</div>
        </div>
      </div>
    </div>
  );
}

